import React, { Component } from "react";
import { Link } from "react-router-dom";
import CESSVG from "../../Assets/svg/CESSVG.svg";
import SignUpPageImage from "../../Assets/jpeg/SignUpPageOverlayImage.jpg";
import { withRouterHooks } from "../../withRouters/withRoutersHook";
import { SignupAction } from "../../Actions/Authentication";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Loader2, Eye, EyeOff, ArrowRight } from "lucide-react";

class SignUp extends Component {
  state = {
    first_name: "",
    last_name: "",
    middle_name: "",
    company_name: "",
    email: "",
    password: "",
    confirm_password: "",
    acceptTerms: false,
    signingup: false,
    showPassword: false,
    formError: null,
  };

  static propTypes = {
    SignupAction: PropTypes.func.isRequired,
    isSignupSuccess: PropTypes.bool,
    navigate: PropTypes.func.isRequired,
  };

  onSubmit = (e) => {
    e.preventDefault();
    const {
      first_name, last_name, middle_name, company_name,
      email, password, confirm_password, acceptTerms,
    } = this.state;

    if (!acceptTerms) {
      this.setState({ formError: "Please accept the terms and conditions." });
      return;
    }
    if (!email || !password || !confirm_password) {
      this.setState({ formError: "Please fill in all required fields." });
      return;
    }

    this.setState({ signingup: true, formError: null });
    setTimeout(() => this.setState({ signingup: false }), 3000);

    this.props.SignupAction(
      first_name, last_name, middle_name, company_name,
      email, password, confirm_password
    );
  };

  onChange = (e) => this.setState({ [e.target.name]: e.target.value });

  splitFullName = (fullName) => {
    const nameParts = fullName.trim().split(" ");
    let firstName = "", middleName = "", lastName = "";
    if (nameParts.length === 1) {
      firstName = nameParts[0];
    } else if (nameParts.length === 2) {
      firstName = nameParts[0];
      lastName = nameParts[1];
    } else {
      firstName = nameParts[0];
      middleName = nameParts.slice(1, -1).join(" ");
      lastName = nameParts[nameParts.length - 1];
    }
    this.setState({ first_name: firstName, middle_name: middleName, last_name: lastName });
  };

  onCheckboxChange = (e) => {
    this.setState({ acceptTerms: e.target.checked });
  };

  componentDidUpdate(prevProps) {
    if (this.props.isSignupSuccess && this.props.isSignupSuccess !== prevProps.isSignupSuccess) {
      this.props.navigate("/verify-email");
    }
  }

  toggleShowPassword = () => {
    this.setState((prev) => ({ showPassword: !prev.showPassword }));
  };

  render() {
    const { company_name, email, password, confirm_password, showPassword, signingup, formError } = this.state;
    const full_name = [this.state.first_name, this.state.middle_name, this.state.last_name]
      .filter(Boolean).join(" ");

    return (
      <div className="flex w-full min-h-screen bg-white">

        {/* Left panel */}
        <div className="flex flex-col flex-1 min-h-screen px-8 py-10 lg:px-16 overflow-y-auto">

          {/* Logo */}
          <div className="flex items-center gap-2.5 mb-auto">
            <img src={CESSVG} alt="Logo" className="w-8 h-8" />
            <span className="font-SFPro font-bold text-gray-900 text-lg tracking-tight">CX2 AI</span>
          </div>

          {/* Form */}
          <div className="w-full max-w-[380px] mx-auto py-10">
            <h1 className="font-SFPro font-bold text-[2rem] leading-tight text-gray-900 mb-2">
              Create an account
            </h1>
            <p className="font-inter text-sm text-gray-400 mb-8">
              Fill in your details to get started.
            </p>

            {formError && (
              <div className="flex items-center gap-2 px-4 py-3 mb-6 bg-red-50 border border-red-100 rounded-xl text-sm font-inter text-red-600">
                {formError}
              </div>
            )}

            <form onSubmit={this.onSubmit} className="flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="font-inter font-medium text-sm text-gray-700">Full Name</label>
                <input
                  type="text"
                  name="full_name"
                  value={full_name}
                  onChange={(e) => this.splitFullName(e.target.value)}
                  placeholder="First, middle and last name"
                  className="w-full h-11 px-4 bg-gray-50 border border-gray-200 rounded-xl text-sm font-inter text-gray-900 placeholder-gray-400 outline-none focus:bg-white focus:border-primary-blue transition-all"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="font-inter font-medium text-sm text-gray-700">Company Name</label>
                <input
                  type="text"
                  name="company_name"
                  value={company_name}
                  onChange={this.onChange}
                  placeholder="Your company's name"
                  className="w-full h-11 px-4 bg-gray-50 border border-gray-200 rounded-xl text-sm font-inter text-gray-900 placeholder-gray-400 outline-none focus:bg-white focus:border-primary-blue transition-all"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="font-inter font-medium text-sm text-gray-700">Email address</label>
                <input
                  type="text"
                  name="email"
                  value={email}
                  onChange={this.onChange}
                  placeholder="you@company.com"
                  className="w-full h-11 px-4 bg-gray-50 border border-gray-200 rounded-xl text-sm font-inter text-gray-900 placeholder-gray-400 outline-none focus:bg-white focus:border-primary-blue transition-all"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="font-inter font-medium text-sm text-gray-700">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={password}
                    onChange={this.onChange}
                    placeholder="••••••••"
                    autoComplete="new-password"
                    className="w-full h-11 px-4 pr-11 bg-gray-50 border border-gray-200 rounded-xl text-sm font-inter text-gray-900 placeholder-gray-400 outline-none focus:bg-white focus:border-primary-blue transition-all"
                  />
                  <button
                    type="button"
                    onClick={this.toggleShowPassword}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="font-inter font-medium text-sm text-gray-700">Confirm Password</label>
                <input
                  type={showPassword ? "text" : "password"}
                  name="confirm_password"
                  value={confirm_password}
                  onChange={this.onChange}
                  placeholder="••••••••"
                  className="w-full h-11 px-4 bg-gray-50 border border-gray-200 rounded-xl text-sm font-inter text-gray-900 placeholder-gray-400 outline-none focus:bg-white focus:border-primary-blue transition-all"
                />
              </div>

              <label className="flex items-start gap-3 cursor-pointer mt-1">
                <input
                  type="checkbox"
                  checked={this.state.acceptTerms}
                  onChange={this.onCheckboxChange}
                  className="mt-0.5 w-4 h-4 rounded border-gray-300 text-primary-blue accent-primary-blue"
                />
                <span className="font-inter text-sm text-gray-500">
                  I agree to the{" "}
                  <Link to="#" className="text-primary-blue hover:underline">terms</Link>{" "}
                  and{" "}
                  <Link to="#" className="text-primary-blue hover:underline">conditions</Link>
                </span>
              </label>

              <button
                type="submit"
                disabled={signingup}
                className="mt-2 flex items-center justify-center gap-2 w-full h-11 bg-primary-blue text-white font-inter font-semibold text-sm rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
              >
                {signingup ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Creating account…</>
                ) : (
                  <>Create account <ArrowRight className="w-4 h-4" /></>
                )}
              </button>
            </form>

            <p className="mt-6 text-center font-inter text-sm text-gray-400">
              Already have an account?{" "}
              <Link to="/login" className="text-primary-blue font-semibold hover:underline">Sign in</Link>
            </p>
          </div>

          <p className="font-inter text-xs text-gray-300 text-center mt-auto">
            © {new Date().getFullYear()} CX2 AI. All rights reserved.
          </p>
        </div>

        {/* Right panel */}
        <div className="hidden lg:flex lg:w-[52%] relative overflow-hidden sticky top-0 h-screen">
          <img src={SignUpPageImage} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#0D6EFD]/60 to-gray-900/70" />
          <div className="relative z-10 flex flex-col justify-end h-full p-12">
            <div className="backdrop-blur-sm bg-white/10 border border-white/20 rounded-2xl p-6 max-w-sm">
              <p className="font-SFPro font-semibold text-white text-lg leading-snug mb-3">
                "Set up in minutes and start delivering smarter customer experiences."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white text-xs font-bold">CX</div>
                <div>
                  <p className="font-inter font-medium text-white text-sm">New Customers</p>
                  <p className="font-inter text-white/60 text-xs">Getting started</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  isSignupSuccess: state.authentication?.isSignupSuccess,
});

export default withRouterHooks(connect(mapStateToProps, { SignupAction })(SignUp));
